import { ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router'
import { Box, Typography } from '@mui/material'
import { AuthProvider, useAuth } from './auth/AuthContext.js'
import { LoginPage } from './pages/LoginPage.js'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js'
import { ResetPasswordPage } from './pages/ResetPasswordPage.js'
import { AppLayout } from './layout/AppLayout.js'
import { ChatPage } from './pages/ChatPage.js'
import { AdminLayout } from './pages/admin/AdminLayout.js'
import { AdminUsersSection } from './pages/admin/AdminUsersSection.js'
import { AdminOrganizationsSection } from './pages/admin/AdminOrganizationsSection.js'
import { AdminOrganizationDetail } from './pages/admin/AdminOrganizationDetail.js'
import { AdminAssignmentsSection } from './pages/admin/AdminAssignmentsSection.js'
import { AdminModelsSection } from './pages/admin/AdminModelsSection.js'

const EmptyState = () => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography color="text.secondary">Wähle einen Chat oder starte einen neuen.</Typography>
  </Box>
)

// Deep-Link-Schutz für die System-Sections (Nutzer, Zuweisungen, Modelle) —
// Org-Admins landen auf ihrer Organisationsliste. Die API lehnt die Aufrufe
// ohnehin mit 403 ab; das hier verhindert nur die leere Shell.
const RequireSystemAdmin = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  if (user?.role !== 'system-admin') return <Navigate to="/settings/organizations" replace />
  return children
}

// Startpunkt des Einstellungsbereichs je Rolle: Systemadmins bei den Nutzern,
// Org-Admins direkt bei ihren Organisationen.
const SettingsIndexRedirect = () => {
  const { user } = useAuth()
  return <Navigate to={user?.role === 'system-admin' ? 'users' : 'organizations'} replace />
}

export const App = () => (
  <AuthProvider>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route element={<AppLayout />}>
        <Route index element={<EmptyState />} />
        <Route path="/chats/:chatId" element={<ChatPage />} />
        {/* Alt-Links auf den früheren Adminbereich weiterleiten. */}
        <Route path="/admin/*" element={<Navigate to="/settings" replace />} />
        <Route path="/settings" element={<AdminLayout />}>
          <Route index element={<SettingsIndexRedirect />} />
          <Route
            path="users"
            element={
              <RequireSystemAdmin>
                <AdminUsersSection />
              </RequireSystemAdmin>
            }
          />
          <Route path="organizations" element={<AdminOrganizationsSection />} />
          {/* Ohne Section auf „Allgemein“ umleiten (ältere Links, OAuth-Callback). */}
          <Route path="organizations/:orgId" element={<Navigate to="general" replace />} />
          <Route path="organizations/:orgId/:section" element={<AdminOrganizationDetail />} />
          <Route
            path="assignments"
            element={
              <RequireSystemAdmin>
                <AdminAssignmentsSection />
              </RequireSystemAdmin>
            }
          />
          <Route
            path="models"
            element={
              <RequireSystemAdmin>
                <AdminModelsSection />
              </RequireSystemAdmin>
            }
          />
        </Route>
      </Route>
    </Routes>
  </AuthProvider>
)
